import React, { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { yearlyTotals, objectifs, tamData, TAM_TOTAL, valorisation, preLaunchBudget, LICORNE_YEAR, SAAS_PRIX, tauxParAnnee, APP_PRIX, preSeedInvestment, objectifsY1, tractionActuelle } from '../mock';
import { formatCurrency, formatCompact, formatCompactMobile } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import { Sparkles, TrendingUp, Target, DollarSign, Zap, Award, Users, Calendar, Rocket, FileDown } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import AnimatedCounter from '../components/AnimatedCounter';
import ProgressBar from '../components/ProgressBar';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { investorTexts } from '../investorTranslations';

export default function InvestorOnePager() {
  const { language } = useLanguage();
  const t = investorTexts[language];
  const isMobile = useIsMobile();
  const handleDownload = useCallback(() => { window.print(); }, []);
  const fmt = (v) => isMobile ? formatCompactMobile(v) : formatCompact(v);
  const total5Years = yearlyTotals.reduce((acc, year) => ({
    revenus: acc.revenus + year.revenus,
    baiia: acc.baiia + year.baiia,
    gmv: acc.gmv + year.gmvRegulier + year.gmvSezzle
  }), { revenus: 0, baiia: 0, gmv: 0 });

  const lastYear = yearlyTotals[yearlyTotals.length - 1] || {};
  const baiiaMargin = lastYear.revenus ? Math.round(lastYear.baiia / lastYear.revenus * 100) : 0;
  const publicTotal = objectifs.total - objectifs.prive;
  const y5Val = valorisation.find(v => v.annee === 'Y5') || {};
  const licorneVal = valorisation.find(v => v.annee === LICORNE_YEAR) || {};

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Print styles */}
      <style>{`
        @media print {
          /* Hide all UI chrome */
          .onepager-download-btn, [data-testid="sticky-navbar"], nav, .sticky,
          [data-testid="sidebar"], aside, [data-testid="bottom-navigation"],
          .bottom-navigation { display: none !important; }
          /* Full width - remove sidebar flex layout */
          body, html { margin: 0 !important; padding: 0 !important; }
          .App, .App > div { display: block !important; }
          .flex.min-h-screen { display: block !important; }
          main { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
          /* Hide background fox patterns */
          .fixed.inset-0.pointer-events-none { display: none !important; }
          /* Preserve colors */
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* Ensure content fits */
          .min-h-screen { min-height: auto !important; padding: 8px !important; }
          .max-w-6xl { max-width: 100% !important; margin: 0 auto !important; }
        }
      `}</style>

      {/* Download button */}
      <button
        onClick={handleDownload}
        data-testid="onepager-download-btn"
        className="onepager-download-btn fixed top-16 right-4 z-40 flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-black transition-all duration-300 shadow-[0_0_20px_rgba(132,204,22,0.4)] hover:shadow-[0_0_35px_rgba(132,204,22,0.6)] hover:scale-105"
        style={{ background: 'linear-gradient(135deg, #84cc16, #06b6d4)' }}
      >
        <FileDown size={18} />
        {language === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
      </button>

      <div className="max-w-6xl mx-auto">
        <PartieBadge path="/synthese-investisseur" />
        <div className="space-y-8">
        {/* Header Pré-seed */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-1.5 bg-purple-500/20 border border-purple-500/40 rounded-full">
            <p className="text-xs font-bold text-purple-300 uppercase tracking-widest">
              {language === 'fr' ? 'Vertical #1 — Repas scolaires & garderies (Initiateur)' : 'Vertical #1 — School & Daycare Meals (Initiator)'}
            </p>
          </div>
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-lime-500/20 via-cyan-500/20 to-orange-500/20 border border-lime-500/50 rounded-2xl px-6 sm:px-8 py-3 sm:py-4 mb-6">
            <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-lime-400 animate-pulse" />
            <div className="text-center leading-tight">
              <p className="text-lg sm:text-2xl font-bold text-lime-400">{language === 'fr' ? 'Pré-seed 500K' : 'Pre-seed $500K'}</p>
              <p className="text-base sm:text-xl font-bold text-white">{language === 'fr' ? '6 mois — Sept→Fév' : '6 months — Sep→Feb'}</p>
              <p className="text-xs sm:text-sm text-cyan-400">{language === 'fr' ? '~10% equity • Ronde seed Février' : '~10% equity • Seed round February'}</p>
            </div>
            <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-400 animate-pulse" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t.syntheseInvestisseur}
          </h1>
          <p className="text-xl text-gray-400">
            {t.planComplet}
          </p>
        </div>

        {/* Section 1 - L'Opportunité */}
        <Card className="bg-gradient-to-br from-lime-500/10 via-black/60 to-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8 text-lime-400" />
              <CardTitle className="text-2xl text-white">
                {t.opportunite}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Encadré FLASHY - Marché fragmenté - UNIFIÉ */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-lime-500/60 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-orange-500/15 p-4 sm:p-8 shadow-[0_0_50px_rgba(168,213,48,0.4)]">
              {/* Décorations */}
              <div className="absolute top-0 right-0 text-8xl opacity-5">⚡</div>
              <div className="absolute bottom-0 left-0 text-8xl opacity-5">🎯</div>
              
              <div className="relative space-y-6">
                {/* Titre */}
                <div className="text-center mb-6">
                  <div className="inline-block px-6 py-2 bg-gradient-to-r from-lime-500 via-cyan-500 to-orange-500 rounded-full mb-4">
                    <h3 className="text-xl font-bold text-black">{t.marcheFragmenteTitre}</h3>
                  </div>
                </div>
                
                {/* Texte unifié */}
                <div className="space-y-4 text-center max-w-4xl mx-auto">
                  <p className="text-gray-100 leading-relaxed">{t.marcheP1}</p>
                  <p className="text-gray-100 leading-relaxed">{t.marcheP2}</p>
                  
                  <div className="py-6">
                    <div className="w-16 h-1 bg-gradient-to-r from-lime-500 to-cyan-500 mx-auto mb-4 rounded-full"></div>
                    <p className="text-lime-400 font-bold text-lg mb-4">{t.marcheRevolution}</p>
                    <p className="text-gray-100 leading-relaxed">{t.marcheDescription}</p>
                  </div>
                  
                  <div className="relative p-6 bg-black/30 rounded-xl border border-lime-500/40">
                    <p className="text-white leading-relaxed font-medium text-lg">{t.marcheConclusion}</p>
                  </div>
                </div>
                
                {/* Footer gradient */}
                <div className="flex items-center gap-2 pt-4">
                  <div className="flex-1 h-2 bg-gradient-to-r from-lime-500 via-cyan-500 to-orange-500 rounded-full shadow-[0_0_20px_rgba(168,213,48,0.6)]"></div>
                  <span className="text-sm font-bold text-lime-400 px-4 py-1 bg-black/40 rounded-full border border-lime-500/50">{t.opportuniteUnique}</span>
                  <div className="flex-1 h-2 bg-gradient-to-r from-orange-500 via-cyan-500 to-lime-500 rounded-full shadow-[0_0_20px_rgba(168,213,48,0.6)]"></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-black/40 rounded-lg border border-orange-500/20">
                <h3 className="text-orange-400 font-semibold mb-2">{t.probleme}</h3>
                <p className="text-sm text-gray-300">{t.problemeDesc}</p>
              </div>
              <div className="p-4 bg-black/40 rounded-lg border border-lime-500/20">
                <h3 className="text-lime-400 font-semibold mb-2">{t.solution}</h3>
                <p className="text-sm text-gray-300">{t.solutionDesc}</p>
              </div>
              <div className="p-4 bg-black/40 rounded-lg border border-cyan-500/20">
                <h3 className="text-cyan-400 font-semibold mb-2">{t.marche}</h3>
                <p className="text-sm text-gray-300">{t.marcheDesc}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2 - Le Modèle (avec Licorne GMV) */}
        <Card className="bg-gradient-to-br from-cyan-500/10 via-black/60 to-black/60 border-cyan-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-cyan-400" />
              <CardTitle className="text-2xl text-white">
                {t.leModele}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* KPIs Y1 — Scénario Réaliste */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-lime-500/20 to-lime-500/5 rounded-xl border border-lime-500/30">
                <p className="text-sm text-gray-400 mb-2">{language === 'fr' ? 'GMV annualisé M12' : 'M12 annualized GMV'}</p>
                <p className="text-2xl sm:text-4xl font-bold text-lime-400 mb-1">
                  <AnimatedCounter value={objectifsY1.realiste.m12.gmvAnnualise / 1000000} duration={3000} decimals={0} suffix="M $" />
                </p>
                <p className="text-xs text-lime-500">{language === 'fr' ? 'Réaliste — 3 000 établ.' : 'Realistic — 3,000 inst.'}</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 rounded-xl border border-cyan-500/30">
                <p className="text-sm text-gray-400 mb-2">{language === 'fr' ? 'Revenu annualisé M12' : 'M12 annualized revenue'}</p>
                <p className="text-2xl sm:text-4xl font-bold text-cyan-400 mb-1">
                  <AnimatedCounter value={objectifsY1.realiste.revenuAnnualiseM12 / 1000000} duration={3000} decimals={2} suffix="M $" />
                </p>
                <p className="text-xs text-cyan-500">{language === 'fr' ? `Minimum: ${(objectifsY1.minimum.revenuAnnualiseM12 / 1000000).toFixed(2)}M $` : `Minimum: $${(objectifsY1.minimum.revenuAnnualiseM12 / 1000000).toFixed(2)}M`}</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-orange-500/20 to-orange-500/5 rounded-xl border border-orange-500/30">
                <p className="text-sm text-gray-400 mb-2">{language === 'fr' ? 'Pré-seed levée' : 'Pre-seed raise'}</p>
                <p className="text-2xl sm:text-4xl font-bold text-orange-400 mb-1">500K $</p>
                <p className="text-xs text-orange-500">{language === 'fr' ? '~10% equity • 6 mois' : '~10% equity • 6 months'}</p>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Section 3 - La Traction */}
        <Card className="bg-gradient-to-br from-orange-500/10 via-black/60 to-black/60 border-orange-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Rocket className="h-8 w-8 text-orange-400" />
              <CardTitle className="text-2xl text-white">
                {t.laTraction}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lime-400 font-semibold mb-3">{t.objectifs5ans}</h4>
                <div className="space-y-3">
                  <ProgressBar value={objectifs.qc} max={tamData.qc.total} label={`${objectifs.qc.toLocaleString('fr-CA')} ${t.ecolesQuebec}`} color="lime" />
                  <ProgressBar value={objectifs.on} max={tamData.on.total} label={`${objectifs.on.toLocaleString('fr-CA')} ${t.ecolesOntario}`} color="cyan" />
                  <ProgressBar value={objectifs.ab} max={tamData.ab.total} label={`${objectifs.ab.toLocaleString('fr-CA')} ${language === 'fr' ? 'écoles Alberta' : 'Alberta schools'}`} color="orange" />
                  <ProgressBar value={objectifs.bc} max={tamData.bc.total} label={`${objectifs.bc} ${t.ecolesBC}`} color="purple" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <ProgressBar value={objectifs.mb} max={tamData.mb.total} label={`${objectifs.mb} MB`} color="yellow" />
                    <ProgressBar value={objectifs.sk} max={tamData.sk.total} label={`${objectifs.sk} SK`} color="teal" />
                    <ProgressBar value={objectifs.nb + objectifs.ne + objectifs.tnl + objectifs.ipe} max={tamData.nb.total + tamData.ne.total + tamData.tnl.total + tamData.ipe.total} label={`${objectifs.nb + objectifs.ne + objectifs.tnl + objectifs.ipe} Atl.`} color="blue" />
                  </div>
                  <ProgressBar value={objectifs.prive} max={1575} label={`${objectifs.prive} ${language === 'fr' ? 'écoles privées' : 'private schools'}`} color="pink" />
                  <div className="pt-2 border-t border-gray-700">
                    <ProgressBar value={objectifs.total} max={TAM_TOTAL} label={`${objectifs.total.toLocaleString('fr-CA')} ${t.ecolesTotal}`} color="lime" />
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-cyan-400 font-semibold mb-3">{t.jalonsTitle}</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-lime-500">•</span>
                    <span>M6-M7 : {language === 'fr' ? 'Autonomie financière (équilibre + cashflow+)' : 'Financial autonomy (break-even + cashflow+)'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-lime-500">•</span>
                    <span>M12 : 14 CSS QC, ~460 {language === 'fr' ? 'écoles' : 'schools'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span>M21 (Sep 2027) : {language === 'fr' ? 'Entrée Ontario (12 conseils)' : 'Ontario Entry (12 boards)'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500">•</span>
                    <span>M33 (Sep 2028) : {language === 'fr' ? 'Entrée BC + AB' : 'BC + AB Entry'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500">•</span>
                    <span>M60 : {objectifs.total.toLocaleString('fr-CA')} {language === 'fr' ? `écoles (${publicTotal.toLocaleString('fr-CA')} pub. + ${objectifs.prive} privé)` : `schools (${publicTotal.toLocaleString('en-CA')} pub. + ${objectifs.prive} private)`}</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-lime-500/10 rounded-lg border border-lime-500/30">
                  <p className="text-xs text-lime-400 font-semibold">🇨🇦 100% Québec</p>
                  <p className="text-xs text-gray-400">Hébergement, paiements (SonaPay), données + Messagerie RBAC</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FORMULATION CRITIQUE - AUTONOMIE FINANCIÈRE */}
        <div className="relative overflow-hidden rounded-2xl border-2 border-lime-500/60 bg-gradient-to-br from-lime-500/20 via-cyan-500/15 to-orange-500/10 p-4 sm:p-8 shadow-[0_0_60px_rgba(168,213,48,0.5)]">
          <div className="absolute top-0 right-0 text-9xl opacity-5">💰</div>
          <div className="absolute bottom-0 left-0 text-9xl opacity-5">🚀</div>
          
          <div className="relative space-y-4">
            <div className="text-center mb-4">
              <div className="inline-block px-6 py-2 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full mb-3">
                <h3 className="text-lg font-bold text-black">{language === 'fr' ? '🎯 AUTONOMIE FINANCIÈRE RAPIDE' : '🎯 RAPID FINANCIAL AUTONOMY'}</h3>
              </div>
            </div>
            
            <div className="p-6 bg-black/40 rounded-xl border border-lime-500/40">
              <p className="text-white leading-relaxed text-center font-medium">
                {t.autonomieFinanciere}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="p-4 bg-black/30 rounded-lg border border-lime-500/30 text-center">
                <p className="text-lime-400 font-bold text-2xl">M0-M6</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Phase financement' : 'Funding phase'}</p>
                <p className="text-xs text-gray-500">250K$</p>
              </div>
              <div className="p-4 bg-black/30 rounded-lg border border-cyan-500/30 text-center">
                <p className="text-cyan-400 font-bold text-2xl">M6-M7</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Équilibre atteint' : 'Break-even achieved'}</p>
                <p className="text-xs text-lime-400">✅ {language === 'fr' ? 'Cashflow positif' : 'Positive cashflow'}</p>
              </div>
              <div className="p-4 bg-black/30 rounded-lg border border-orange-500/30 text-center">
                <p className="text-orange-400 font-bold text-2xl">M7+</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Croissance autofinancée' : 'Self-funded growth'}</p>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Financement opportuniste' : 'Opportunistic funding'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4 - L'Investissement */}
        <Card className="bg-gradient-to-br from-lime-500/10 via-cyan-500/10 to-orange-500/10 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-lime-400" />
              <CardTitle className="text-2xl text-white">
                {language === 'fr' ? 'L\'Investissement' : 'The Investment'}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lime-400 font-semibold mb-4">{language === 'fr' ? 'Investissement Demandé' : 'Investment Ask'}</h4>
                <div className="p-4 bg-gradient-to-br from-lime-500/15 to-transparent rounded-lg border-2 border-lime-500/40 mb-3">
                  <p className="text-3xl font-bold text-lime-400">{formatCurrency(preLaunchBudget.total)}</p>
                  <p className="text-xs text-gray-400 mt-1">Pre-launch (Avr-Sept 2026)</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-lime-500/20">
                    <span className="text-sm text-gray-300">OPEX</span>
                    <span className="text-sm font-bold text-lime-400">{formatCurrency(preLaunchBudget.opex6mois)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-cyan-500/20">
                    <span className="text-sm text-gray-300">{language === 'fr' ? 'Incitatifs' : 'Incentives'}</span>
                    <span className="text-sm font-bold text-cyan-400">{formatCurrency(preLaunchBudget.incitatifs)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-black/40 rounded-lg border border-orange-500/20">
                    <span className="text-sm text-gray-300">{language === 'fr' ? 'Réserve' : 'Reserve'}</span>
                    <span className="text-sm font-bold text-orange-400">{formatCurrency(preLaunchBudget.reserve)}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-cyan-400 font-semibold mb-4">{language === 'fr' ? 'Objectifs Année 1' : 'Year 1 Objectives'}</h4>
                <div className="space-y-3">
                  <div className="p-4 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-lg border border-cyan-500/30">
                    <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Minimum M12' : 'Minimum M12'}</p>
                    <p className="text-2xl font-bold text-cyan-400 mb-1">{(objectifsY1.minimum.revenuAnnualiseM12 / 1000000).toFixed(2)}M $</p>
                    <p className="text-xs text-gray-500">{objectifsY1.minimum.m12.etablissements.toLocaleString()} {language === 'fr' ? 'établissements' : 'institutions'}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-lime-500/20 to-transparent rounded-lg border border-lime-500/30">
                    <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Réaliste M12 (cible)' : 'Realistic M12 (target)'}</p>
                    <p className="text-2xl font-bold text-lime-400 mb-1">{(objectifsY1.realiste.revenuAnnualiseM12 / 1000000).toFixed(2)}M $</p>
                    <p className="text-xs text-lime-300">{objectifsY1.realiste.m12.etablissements.toLocaleString()} {language === 'fr' ? 'établissements' : 'institutions'}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-300 flex items-start gap-2">
                      <span className="text-lime-500">•</span>
                      <span>{language === 'fr' ? 'Ronde seed Février : ~10% de la valeur établie' : 'Seed round February: ~10% of established value'}</span>
                    </p>
                    <p className="text-sm text-gray-300 flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>{language === 'fr' ? 'Au-delà conditionnel à la traction réelle' : 'Beyond conditional on actual traction'}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métriques rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-black/60 border-lime-500/30">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-lime-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white"><AnimatedCounter value={objectifs.total} duration={2000} /></p>
              <p className="text-xs text-gray-400">{language === 'fr' ? 'Écoles visées' : 'Target schools'}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/60 border-cyan-500/30">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-6 w-6 text-cyan-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{baiiaMargin}%</p>
              <p className="text-xs text-gray-400">{language === 'fr' ? 'Marge BAIIA' : 'EBITDA Margin'}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/60 border-orange-500/30">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-orange-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">M1</p>
              <p className="text-xs text-gray-400">{language === 'fr' ? 'BAIIA positif' : 'Positive EBITDA'}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/60 border-lime-500/30">
            <CardContent className="p-4 text-center">
              <Zap className="h-6 w-6 text-lime-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">100%</p>
              <p className="text-xs text-gray-400">QC (SonaPay)</p>
            </CardContent>
          </Card>
        </div>

        {/* Use of Funds */}
        <Card className="bg-black/60 border-orange-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl text-white">
              {language === 'fr' ? 'Utilisation des Fonds (250K$)' : 'Use of Funds ($250K)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <ProgressBar value={preLaunchBudget.opex6mois} max={preLaunchBudget.total} label={`OPEX (${formatCurrency(preLaunchBudget.opex6mois)})`} color="lime" />
              <ProgressBar value={preLaunchBudget.incitatifs} max={preLaunchBudget.total} label={`${language === 'fr' ? 'Incitatifs' : 'Incentives'} (${formatCurrency(preLaunchBudget.incitatifs)})`} color="cyan" />
              <ProgressBar value={preLaunchBudget.reserve} max={preLaunchBudget.total} label={`${language === 'fr' ? 'Réserve' : 'Reserve'} (${formatCurrency(preLaunchBudget.reserve)})`} color="orange" />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <NavigationControls />
        </div>
      </div>
    </div>
  );
}
